import httpClient from '../httpClient';

export interface Question {
  id: number;
  type: 'MCQ' | 'T/F' | 'Essay' | 'Short';
  difficulty: 'easy' | 'medium' | 'hard';
  topic: string;
  points: number;
  question: string;
  options?: string[];
  correctAnswer?: string;
}

export interface Assessment {
  id: number;
  name: string;
  description: string;
  status: string;
  type: string;
  questions: number;
  time: string;
  passing: string;
  created: string;
}

// "Available" questions in the Create Assessment picker are the same rows that
// power the Question Bank — keep the type aligned so options + correctAnswer
// flow through to the saved assessment.
export type AvailableQuestion = Question;

export interface CarveAssessmentSource {
  id: number;
  name: string;
  questions: number;
}

const QUESTION_BANK_BASE = '/admin/question-bank/questions';
const FEEDBACK_FORMS_BASE = '/admin/feedback/forms';

export type QuestionPayload = Omit<Question, 'id'>;

export interface CreateAssessmentInput {
  title: string;
  description?: string;
  type: string;
  timeLimit?: number | null;
  passingScore?: number | null;
  attemptsAllowed?: number | null;
  randomizeQuestions?: boolean;
  immediateFeedback?: boolean;
  status?: 'draft' | 'published';
  questions: Array<{
    id?: number;
    type: string;
    question: string;
    points?: number;
    options?: string[];
    correctAnswer?: string;
  }>;
}

// Map the UI's high-level question types to the values the backend
// feedback_questions.question_type ENUM actually accepts.
//   - MCQ → multiple_choice_single (the picker marks exactly one correct option)
//   - T/F → multiple_choice_single with ['True','False'] options
//   - Essay → paragraph (long subjective answer)
//   - Short → short_text (brief subjective answer)
const QUESTION_TYPE_MAP: Record<string, string> = {
  MCQ: 'multiple_choice_single',
  'T/F': 'multiple_choice_single',
  Essay: 'paragraph',
  Short: 'short_text',
};

const mapQuestionType = (t: string) => QUESTION_TYPE_MAP[t] || 'short_text';
const SUBJECTIVE_BACKEND_TYPES = new Set(['short_text', 'paragraph', 'slider']);
const CHOICE_BACKEND_TYPES = new Set(['multiple_choice_single', 'multiple_choice_multi']);

const buildAssessmentPayload = (input: CreateAssessmentInput) => {
  const questions = (input.questions || []).map((q) => {
    const backendType = mapQuestionType(q.type);

    // True/False is stored as a single-choice question with synthetic
    // options when the picker didn't supply them.
    let opts = Array.isArray(q.options) ? q.options.filter((o) => o && o.trim()) : [];
    if (q.type === 'T/F' && opts.length === 0) opts = ['True', 'False'];

    const correctIdx = q.correctAnswer && opts.length > 0
      ? opts.indexOf(q.correctAnswer)
      : -1;

    return {
      type: backendType,
      text: q.question,
      required: true,
      options: CHOICE_BACKEND_TYPES.has(backendType) && opts.length > 0 ? opts : null,
      correctAnswers: correctIdx >= 0 ? [correctIdx] : [],
      score: q.points ?? 1,
    };
  });

  // Derive assessmentType from the mix of question types so the backend
  // validator accepts the form: objective (choice only), subjective
  // (short_text/paragraph/slider), or both.
  const hasChoice = questions.some((q) => CHOICE_BACKEND_TYPES.has(q.type));
  const hasSubjective = questions.some((q) => SUBJECTIVE_BACKEND_TYPES.has(q.type));
  const assessmentType = hasChoice && hasSubjective
    ? 'both'
    : hasSubjective
    ? 'subjective'
    : 'objective';

  // feedback_forms.status ENUM is ('draft','active','closed') — translate
  // the UI's "published" intent to the live value the column accepts.
  const dbStatus = input.status === 'published' ? 'active' : 'draft';

  const payload: Record<string, any> = {
    name: input.title,
    description: input.description || '',
    type: 'assessment',
    assessmentType,
    status: dbStatus,
    showCorrectAnswers: !!input.immediateFeedback,
    questions,
  };

  // Carry settings that the feedback_forms schema may not have columns for —
  // the controller ignores unknown keys, but the payload won't need to change
  // once those columns are added.
  if (input.timeLimit != null) payload.timeLimit = input.timeLimit;
  if (input.passingScore != null) payload.passingScore = input.passingScore;
  if (input.attemptsAllowed != null) payload.maxAttempts = input.attemptsAllowed;
  if (input.randomizeQuestions !== undefined) payload.randomize = !!input.randomizeQuestions;

  return payload;
};

export const createAssessment = async (
  input: CreateAssessmentInput
): Promise<{ success: boolean; data?: any; error?: { message: string } | null }> => {
  try {
    const payload = buildAssessmentPayload(input);
    const response = await httpClient.post<{ success: boolean; data: any }>(
      FEEDBACK_FORMS_BASE,
      payload
    );
    return { success: true, data: (response as any)?.data, error: null };
  } catch (error: any) {
    return {
      success: false,
      error: { message: error?.message ?? 'Failed to create assessment' },
    };
  }
};

export const updateAssessment = async (
  id: number,
  input: CreateAssessmentInput
): Promise<{ success: boolean; data?: any; error?: { message: string } | null }> => {
  try {
    const payload = buildAssessmentPayload(input);
    const response = await httpClient.put<{ success: boolean; data: any }>(
      `${FEEDBACK_FORMS_BASE}/${id}`,
      payload
    );
    return { success: true, data: (response as any)?.data, error: null };
  } catch (error: any) {
    return {
      success: false,
      error: { message: error?.message ?? 'Failed to update assessment' },
    };
  }
};

// Map a backend feedback_questions row back to the UI's Question shape.
const mapBackendQuestionToUi = (q: any): Question => {
  const stringifyArray = (arr: unknown[]): string[] => arr.map((v) => String(v));
  const numberifyArray = (arr: unknown[]): number[] =>
    arr.map((v) => Number(v)).filter((n: number) => Number.isFinite(n));

  const parseStringArray = (raw: unknown): string[] | undefined => {
    if (!raw) return undefined;
    if (Array.isArray(raw)) return stringifyArray(raw);
    if (typeof raw === 'string') {
      try {
        const parsed: unknown = JSON.parse(raw);
        return Array.isArray(parsed) ? stringifyArray(parsed) : undefined;
      } catch (_e) {
        return undefined;
      }
    }
    return undefined;
  };

  const parseNumberArray = (raw: unknown): number[] => {
    if (!raw) return [];
    if (Array.isArray(raw)) return numberifyArray(raw);
    if (typeof raw === 'string') {
      try {
        const parsed: unknown = JSON.parse(raw);
        return Array.isArray(parsed) ? numberifyArray(parsed) : [];
      } catch (_e) {
        return [];
      }
    }
    return [];
  };

  const opts: string[] | undefined = parseStringArray(q.options);
  const correctIndexes: number[] = parseNumberArray(q.correct_answers);
  const correctAnswer =
    opts && correctIndexes.length > 0 && opts[correctIndexes[0]] != null
      ? opts[correctIndexes[0]]
      : undefined;

  let uiType: Question['type'] = 'Short';
  if (q.question_type === 'paragraph') uiType = 'Essay';
  else if (q.question_type === 'short_text') uiType = 'Short';
  else if (q.question_type === 'multiple_choice_single' || q.question_type === 'multiple_choice_multi') {
    const looksLikeTF =
      !!opts &&
      opts.length === 2 &&
      opts.every((o: string) => /^(true|false)$/i.test(o));
    uiType = looksLikeTF ? 'T/F' : 'MCQ';
  }

  return {
    id: q.id,
    type: uiType,
    difficulty: 'medium',
    topic: '',
    points: q.score ?? 1,
    question: q.question_text ?? '',
    options: opts,
    correctAnswer,
  };
};

export interface AssessmentDetail {
  id: number;
  title: string;
  description: string;
  status: string;
  immediateFeedback: boolean;
  timeLimit: number | null;
  passingScore: number | null;
  attemptsAllowed: number | null;
  randomizeQuestions: boolean;
  questions: Question[];
}

export const getAssessmentById = async (
  id: number
): Promise<{ success: boolean; data?: AssessmentDetail; error?: { message: string } | null }> => {
  try {
    const response = await httpClient.get<{ success: boolean; data: any }>(
      `${FEEDBACK_FORMS_BASE}/${id}`
    );
    const raw = (response as any)?.data;
    if (!raw) throw new Error('Empty response');

    const toNumOrNull = (v: unknown): number | null => {
      if (v === null || v === undefined || v === '') return null;
      const n = Number(v);
      return Number.isFinite(n) ? n : null;
    };

    const detail: AssessmentDetail = {
      id: raw.id,
      title: raw.name || '',
      description: raw.description || '',
      status: raw.status || 'draft',
      immediateFeedback: !!raw.show_correct_answers,
      timeLimit: toNumOrNull(raw.time_limit_minutes),
      passingScore: toNumOrNull(raw.passing_score),
      attemptsAllowed: toNumOrNull(raw.max_attempts),
      randomizeQuestions: !!raw.randomize_questions,
      questions: Array.isArray(raw.questions)
        ? raw.questions.map(mapBackendQuestionToUi)
        : [],
    };
    return { success: true, data: detail, error: null };
  } catch (error: any) {
    return {
      success: false,
      error: { message: error?.message ?? 'Failed to load assessment' },
    };
  }
};

export const getQuestions = async (params?: {
  search?: string;
  topic?: string;
  type?: string;
}): Promise<{
  success: boolean;
  data?: Question[];
  error?: { message: string } | null;
}> => {
  try {
    const query: string[] = [];
    if (params?.search) query.push(`search=${encodeURIComponent(params.search)}`);
    if (params?.topic) query.push(`topic=${encodeURIComponent(params.topic)}`);
    if (params?.type) query.push(`type=${encodeURIComponent(params.type)}`);
    const url = `${QUESTION_BANK_BASE}${query.length ? `?${query.join('&')}` : ''}`;

    const response = await httpClient.get<{ success: boolean; data: Question[] }>(url);
    const data = (response as any)?.data ?? [];
    return { success: true, data: Array.isArray(data) ? data : [], error: null };
  } catch (error: any) {
    return {
      success: false,
      data: [],
      error: { message: error?.message ?? 'Failed to fetch questions' }
    };
  }
};

export const createQuestion = async (
  payload: QuestionPayload
): Promise<{ success: boolean; data?: Question; error?: { message: string } | null }> => {
  try {
    const response = await httpClient.post<{ success: boolean; data: Question }>(
      QUESTION_BANK_BASE,
      payload
    );
    const data = (response as any)?.data;
    return { success: true, data, error: null };
  } catch (error: any) {
    return {
      success: false,
      error: { message: error?.message ?? 'Failed to create question' }
    };
  }
};

export const updateQuestion = async (
  id: number,
  payload: Partial<QuestionPayload>
): Promise<{ success: boolean; data?: Question; error?: { message: string } | null }> => {
  try {
    const response = await httpClient.put<{ success: boolean; data: Question }>(
      `${QUESTION_BANK_BASE}/${id}`,
      payload
    );
    const data = (response as any)?.data;
    return { success: true, data, error: null };
  } catch (error: any) {
    return {
      success: false,
      error: { message: error?.message ?? 'Failed to update question' }
    };
  }
};

export const deleteQuestion = async (
  id: number
): Promise<{ success: boolean; error?: { message: string } | null }> => {
  try {
    await httpClient.delete(`${QUESTION_BANK_BASE}/${id}`);
    return { success: true, error: null };
  } catch (error: any) {
    return {
      success: false,
      error: { message: error?.message ?? 'Failed to delete question' }
    };
  }
};

export const getAssessments = async (): Promise<{
  success: boolean;
  data?: Assessment[];
  error?: { message: string } | null;
}> => {
  try {
    // Fetch real assessments from admin feedback API (filter to assessment type only)
    const response = await httpClient.get<{ success: boolean; data: any[] }>('/admin/feedback/forms?type=assessment');

    // httpClient.get returns response.data (Axios unwraps), so response = { success, data: [...] }
    const forms = (response as any).data || [];
    const assessments: Assessment[] = (Array.isArray(forms) ? forms : []).map((form: any) => {
      const time = form.time_limit_minutes != null && form.time_limit_minutes !== ''
        ? `${form.time_limit_minutes} min`
        : '-';
      const passing = form.passing_score != null && form.passing_score !== ''
        ? `${form.passing_score}%`
        : '-';
      return {
        id: form.id,
        name: form.name,
        description: form.description || '',
        status: form.status,
        type: form.type === 'assessment' ? 'Assessment' : form.type,
        questions: form.question_count || 0,
        time,
        passing,
        created: form.created_at ? new Date(form.created_at).toLocaleDateString() : '',
      };
    });

    return {
      success: true,
      data: assessments,
      error: null
    };
  } catch (error: any) {
    return {
      success: false,
      error: { message: error?.message ?? 'Failed to fetch assessments' }
    };
  }
};

export const getAvailableQuestions = async (): Promise<{
  success: boolean;
  data?: AvailableQuestion[];
  error?: { message: string } | null;
}> => {
  try {
    const response = await httpClient.get<{ success: boolean; data: Question[] }>(
      QUESTION_BANK_BASE
    );
    const data = (response as any)?.data ?? [];
    return {
      success: true,
      data: Array.isArray(data) ? data : [],
      error: null,
    };
  } catch (error: any) {
    return {
      success: false,
      data: [],
      error: { message: error?.message ?? 'Failed to fetch available questions' }
    };
  }
};

export const getCarveAssessmentSources = async (): Promise<{
  success: boolean;
  data?: CarveAssessmentSource[];
  error?: { message: string } | null;
}> => {
  try {
    // Carve sources are the existing assessments — reuse the same admin
    // endpoint AssessmentList uses, then project onto the carve shape.
    const response = await httpClient.get<{ success: boolean; data: any[] }>(
      `${FEEDBACK_FORMS_BASE}?type=assessment`
    );
    const forms = (response as any)?.data || [];
    const sources: CarveAssessmentSource[] = (Array.isArray(forms) ? forms : []).map((f: any) => ({
      id: f.id,
      name: f.name,
      questions: f.question_count || 0,
    }));
    return { success: true, data: sources, error: null };
  } catch (error: any) {
    return {
      success: false,
      data: [],
      error: { message: error?.message ?? 'Failed to fetch carve assessment sources' },
    };
  }
};
